import { formatNoteDate } from './siteNote';

/**
 * Pens per site from the customer's own farm data (tidaltech/koi#8261):
 * how many pens have fish, and how many our systems sent data from.
 * Leatherback reads them from BigQuery when asked; customers without a
 * tenant project have none.
 */
export interface SitePenCounts {
  siteId: string;
  /** YYYY-MM-DD, the latest day with farm data. */
  date: string;
  pens: number;
  pensWithFish: number;
  pensWithData: number;
}

export interface PenCountsBySite {
  /** The day the counts are for; undefined when there is no farm data. */
  date?: string | undefined;
  get(siteId: string): SitePenCounts | undefined;
}

/**
 * Indexes ListSitePenCounts's answer by site. A site missing from a
 * non-empty answer had no pens that day, so it counts as zero; with an
 * empty answer there is nothing to show.
 */
export function penCountsBySite(counts: readonly SitePenCounts[]): PenCountsBySite {
  const bySite = new Map(counts.map((count) => [count.siteId, count]));
  const date = counts.reduce<string | undefined>(
    (latest, count) => (!latest || count.date > latest ? count.date : latest),
    undefined,
  );
  return {
    date,
    get(siteId) {
      if (!date) return undefined;
      return bySite.get(siteId) ?? { siteId, date, pens: 0, pensWithFish: 0, pensWithData: 0 };
    },
  };
}

/** "12 Oct" for a YYYY-MM-DD day, in local time rather than UTC midnight. */
export function formatPenCountsDate(date: string, now = new Date()): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!match) return '';
  const local = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return formatNoteDate(local.toISOString(), now);
}
